/**
 * Keep Sold as / Bought as on the same identifier when a row's columns shift.
 * These operations never fall back to the organisation default column.
 * A value that did not already hold the role stays unselected.
 */
import type { VariantMappingRow } from '@/shared/types/variantMapping';
import { buildVariantManagementRows } from '@/shared/variantMapping/variantManagementColumns';

export const DETACHED_DESIGNATION_KEY = '~:0';
export const DETACHED_DESIGNATION_TYPE = '~';

export type MatrixIdentifier = { type: string; value: string };

export type StoredDesignationPin = {
  columnKey: string;
  identifierType: string;
} | null;

export type RoleDesignationPins = {
  soldAs: StoredDesignationPin;
  boughtAs: StoredDesignationPin;
};

type CatalogEntry = {
  master_product_id: string;
  linked_identifiers?: readonly { type: string; value: string; linkId?: string }[];
};

function parseColumnKey(columnKey: string | null | undefined): { type: string; index: number } | null {
  if (!columnKey) return null;
  const separator = columnKey.indexOf(':');
  if (separator <= 0) return null;
  const type = columnKey.slice(0, separator).trim().toLowerCase();
  const index = Number(columnKey.slice(separator + 1));
  if (!type || !Number.isInteger(index) || index < 0) return null;
  return { type, index };
}

export function identifierAtColumn(
  identifiers: readonly MatrixIdentifier[],
  columnKey: string | null | undefined,
): MatrixIdentifier | null {
  const parsed = parseColumnKey(columnKey);
  if (!parsed) return null;
  const values = identifiers.filter((identifier) => identifier.type === parsed.type);
  return values[parsed.index] ?? null;
}

export function columnKeyForIdentifier(
  identifiers: readonly MatrixIdentifier[],
  identifier: MatrixIdentifier | null,
): string | null {
  if (!identifier) return null;
  const values = identifiers.filter((item) => item.type === identifier.type);
  const index = values.findIndex((item) => item.value === identifier.value);
  if (index < 0) return null;
  return `${identifier.type}:${index}`;
}

const DETACHED_PIN: StoredDesignationPin = {
  columnKey: DETACHED_DESIGNATION_KEY,
  identifierType: DETACHED_DESIGNATION_TYPE,
};

/**
 * Column key to store after `before` becomes `after`.
 * The pin stays on the identifier that held the role. If that identifier is
 * gone, or none held the role, the role stays unselected.
 */
export function pinAfterIdentifierChange(input: {
  before: readonly MatrixIdentifier[];
  after: readonly MatrixIdentifier[];
  effectiveColumnKey: string | null;
}): StoredDesignationPin {
  const pinned = identifierAtColumn(input.before, input.effectiveColumnKey);
  if (!pinned) return DETACHED_PIN;
  const nextKey = columnKeyForIdentifier(input.after, pinned);
  if (!nextKey) return DETACHED_PIN;
  return { columnKey: nextKey, identifierType: pinned.type };
}

function pinsForBothRoles(input: {
  before: readonly MatrixIdentifier[];
  after: readonly MatrixIdentifier[];
  soldEffectiveKey: string | null;
  boughtEffectiveKey: string | null;
}): RoleDesignationPins {
  return {
    soldAs: pinAfterIdentifierChange({
      before: input.before,
      after: input.after,
      effectiveColumnKey: input.soldEffectiveKey,
    }),
    boughtAs: pinAfterIdentifierChange({
      before: input.before,
      after: input.after,
      effectiveColumnKey: input.boughtEffectiveKey,
    }),
  };
}

export function designationPinsAfterDisassociate(input: {
  sourceIdentifiers: readonly MatrixIdentifier[];
  removed: MatrixIdentifier;
  soldEffectiveKey: string | null;
  boughtEffectiveKey: string | null;
}): { source: RoleDesignationPins; moved: RoleDesignationPins } {
  const sourceAfter = input.sourceIdentifiers.filter(
    (identifier) => !(identifier.type === input.removed.type && identifier.value === input.removed.value),
  );
  const shared = {
    before: input.sourceIdentifiers,
    soldEffectiveKey: input.soldEffectiveKey,
    boughtEffectiveKey: input.boughtEffectiveKey,
  };
  return {
    source: pinsForBothRoles({ ...shared, after: sourceAfter }),
    // The new EVE ID follows the organisation default column for both roles.
    moved: { soldAs: null, boughtAs: null },
  };
}

function compareLinkId(left: string | undefined, right: string | undefined): number {
  const a = left ?? '';
  const b = right ?? '';
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

export function survivorIdentifiersAfterAssociate(input: {
  catalog: ReadonlyArray<CatalogEntry>;
  mappingRows: readonly VariantMappingRow[];
  sourceMasterProductId: string;
  survivorMasterProductId: string;
}): MatrixIdentifier[] {
  const sourceLinks =
    input.catalog.find((entry) => entry.master_product_id === input.sourceMasterProductId)?.linked_identifiers ?? [];
  const catalog = input.catalog.map((entry) => {
    if (entry.master_product_id === input.sourceMasterProductId) {
      return { ...entry, linked_identifiers: [] as CatalogEntry['linked_identifiers'] };
    }
    if (entry.master_product_id === input.survivorMasterProductId) {
      const merged = [...(entry.linked_identifiers ?? []), ...sourceLinks].sort((left, right) =>
        compareLinkId(left.linkId, right.linkId),
      );
      return { ...entry, linked_identifiers: merged };
    }
    return entry;
  });
  const mappingRows = input.mappingRows.map((row) => {
    if (row.matchedVariant?.master_product_id !== input.sourceMasterProductId || !row.matchedVariant) return row;
    return {
      ...row,
      matchedVariant: { ...row.matchedVariant, master_product_id: input.survivorMasterProductId },
    };
  });
  const survivor = buildVariantManagementRows(catalog, mappingRows).find(
    (row) => row.variantId === input.survivorMasterProductId,
  );
  return (survivor?.identifiers ?? []).map((identifier) => ({ type: identifier.type, value: identifier.value }));
}

export function designationPinsAfterAssociate(input: {
  survivorIdentifiers: readonly MatrixIdentifier[];
  mergedIdentifiers: readonly MatrixIdentifier[];
  soldEffectiveKey: string | null;
  boughtEffectiveKey: string | null;
}): RoleDesignationPins {
  return pinsForBothRoles({
    before: input.survivorIdentifiers,
    after: input.mergedIdentifiers,
    soldEffectiveKey: input.soldEffectiveKey,
    boughtEffectiveKey: input.boughtEffectiveKey,
  });
}

export type DesignationPinJson = {
  column_key: string;
  identifier_type: string;
} | null;

export function designationPinJson(pin: StoredDesignationPin): DesignationPinJson {
  if (!pin) return null;
  return { column_key: pin.columnKey, identifier_type: pin.identifierType };
}

export function rolePinsJson(pins: RoleDesignationPins): {
  sold_as: DesignationPinJson;
  bought_as: DesignationPinJson;
} {
  return {
    sold_as: designationPinJson(pins.soldAs),
    bought_as: designationPinJson(pins.boughtAs),
  };
}
